<script lang="ts">
  import type { PaginationDetails } from "@shared/types/Pagination";
  import { createEventDispatcher } from "svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import { uniq } from "lodash";

  export let pagination: PaginationDetails = {
    goToPage: number => {},
    page: 0,
    perPage: 0,
    totalEntries: 0,
  };

  export let showTotals = false;
  export let firstSixOnly: boolean = false;

  let dispatch = createEventDispatcher();

  let offsetStart: number;
  let offsetEnd: number;
  let hasPreviousPage: boolean;
  let hasNextPage: boolean;
  let pageNumbers = [];
  let lastPage;

  $: {
    lastPage = Math.ceil(pagination.totalEntries / pagination.perPage);
    offsetStart = pagination.perPage * (pagination.page - 1) + 1;
    offsetEnd = offsetStart + pagination.perPage - 1;
    if (offsetEnd > pagination.totalEntries) {
      offsetEnd = pagination.totalEntries;
    }
  }

  let paginationEventDispatch = page => {
    dispatch("paginationEvent", { page });
  };

  function nextPage() {
    if (!hasNextPage) {
      return;
    }
    let newPage = pagination.page + 1;
    handlePageChange(newPage);
  }

  function previousPage() {
    if (!hasPreviousPage) {
      return;
    }
    handlePageChange(pagination.page - 1);
  }

  const handlePageChange = newPage => {
    pagination.goToPage && pagination.goToPage(newPage);
    paginationEventDispatch(newPage);
  };

  $: {
    hasPreviousPage = pagination && pagination.page > 1;
    hasNextPage =
      pagination &&
      offsetStart + pagination.perPage <= pagination.totalEntries &&
      Number(pagination.totalEntries) > Number(pagination.perPage);
    pageNumbers = [];

    let startPage = pagination.page - 2 >= 1 ? pagination.page - 2 : 1;
    let endPage = pagination.page + 2 <= lastPage ? pagination.page + 2 : lastPage;
    if (firstSixOnly) {
      endPage = startPage + 5;
      if (endPage > lastPage) {
        endPage = lastPage;
      }
    }

    if (pagination.page === lastPage && lastPage > 5) {
      pageNumbers.push(1);
      pageNumbers.push("...");
      for (let i = lastPage - 2; i <= lastPage; i++) {
        pageNumbers.push(i);
      }
    } else {
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (!firstSixOnly) {
        if (endPage + 2 < lastPage) {
          pageNumbers.push("...");
        }

        if (lastPage > 1) {
          pageNumbers.push(lastPage - 1);
        }

        pageNumbers.push(lastPage);
      }
      pageNumbers = uniq(pageNumbers);
    }
  }

  const fizzIsActive = pageNumber =>
    pagination.page == pageNumber || (!pagination.page && pageNumber == 1);

</script>

<style>
  a:hover {
    cursor: pointer;
  }

</style>

<div class="fizz-pagination">
  <span>
    {#if showTotals && offsetStart}
      <span class="margin-right--smaller">
        <span>{offsetStart}-{offsetEnd}</span>
        {#if pagination.showTotals}
          <span>
            of
            <span class="w700">{pagination.totalEntries}</span>
          </span>
        {/if}
      </span>
    {/if}
  </span>
  <button
    id="previous-page"
    class="fizz-icon-button"
    on:click={previousPage}
    data-testid={"previous-page"}
    disabled={!hasPreviousPage}
  >
    <Icon width="16" height="16" type="ChevronLeft" className="fizz-icon-16" />
  </button>
  <ol class="fizz-pagination-list">
    {#each pageNumbers as pageNumber}
      {#if Number.isInteger(pageNumber)}
        <li
          data-page="{pagination.page} of {lastPage}"
          on:click={() => handlePageChange(pageNumber)}
          class:fizz-is-active={fizzIsActive(pageNumber)}
        >
          <a href={"javascript:;"}>{pageNumber}</a>
        </li>
      {:else}
        <li>
          <a href={"javascript:;"}>&mldr;</a>
        </li>
      {/if}
    {/each}
  </ol>
  <button
    id="next-page"
    class="fizz-icon-button"
    on:click={nextPage}
    data-testid={"next-page"}
    disabled={!hasNextPage}
  >
    <Icon width="16" height="16" type="ChevronRight" className="fizz-icon-16" fill="black" />
  </button>
</div>
